import { ICustomisationSettings } from './customisation.interface';
import { customisation as defaultCustomisation } from './customisation.settings';
import { customisation as lfauCustomisation } from './customisation.lfau.settings';
import { Injectable } from '@angular/core';
import { aggregator } from '@env/aggregator/aggregator';
import { Aggregator } from '@liberty-financial/agp-shared';
import { AssetType, PaygIncomeAttributes, ProductDetails, ProductFeeType, TextAttributes } from '@shared/enums/customisation.enum';
import { OpportunityType } from '@shared/models/opportunity';

@Injectable({
  providedIn: 'root'
})
export class CustomisationService {
  private customisation: ICustomisationSettings;

  constructor() {
    if (aggregator.name === Aggregator.LFAU) {
      this.customisation = lfauCustomisation;
    } else {
      this.customisation = defaultCustomisation;
    }
  }

  getCustomisation(): ICustomisationSettings {
    return this.customisation;
  }

  getCustomisationForPersonDetails() {
    const personCustomisation = this.customisation.applicant.personDetails;
    return personCustomisation;
  }

  getCustomisationForAdditionalDetails() {
    const additionalCustomisation = this.customisation.applicant.furtherDetails;
    return additionalCustomisation;
  }

  getCustomisationForPurposeType(opportunityType: OpportunityType) {
    let purposeTypeCustomisation;
    switch (opportunityType) {
      case 'Home':
        purposeTypeCustomisation = this.customisation.purposeType.homeLoan;
        break;
      case 'Commercial':
        purposeTypeCustomisation = this.customisation.purposeType.commercialLoan;
        break;
      case 'SMSF':
        purposeTypeCustomisation = this.customisation.purposeType.smsfLoan;
        break;
      case 'Car':
        purposeTypeCustomisation = this.customisation.purposeType.motorLoan;
        break;
      case 'Personal':
        purposeTypeCustomisation = this.customisation.purposeType.personalLoan;
        break;
      case 'Insurance':
        purposeTypeCustomisation = this.customisation.purposeType.insuranceLoan;
        break;
      case 'Refix':
        purposeTypeCustomisation = this.customisation.purposeType.refixLoan;
        break;
    }
    return purposeTypeCustomisation;
  }

  getCustomisationForOpportunityDetails() {
    const opportunityCustomisation = this.customisation.summary.opportunityDetail;
    return opportunityCustomisation;
  }

  getCustomisationForPropertyPurchaseDetails() {
    const propertyPurchaseAttributes = this.customisation.purpose.propertyPurchaseDetails;
    return propertyPurchaseAttributes;
  }

  getCustomisationForPropertyPurchaseFinancials() {
    const propertyPurchaseFinancials = this.customisation.purpose.propertyPurchaseFinancials;
    return propertyPurchaseFinancials;
  }

  getCustomisationForSideMenu() {
    const menuAttributes = this.customisation.menu.sideMenu;
    return menuAttributes;
  }

  getText(key: TextAttributes): string {
    return this.customisation.text[key];
  }

  getCustomisationForShowSubmissionErrorMessage(): boolean {
    return this.customisation.summary.showSubmissionErrorMessage;
  }

  getCustomisationForProductSearchResultDetails() {
    const productSearchResultAttributes = this.customisation.product.searchResultDetails;
    return productSearchResultAttributes;
  }

  getCustomisationForProductType() {
    const productType = this.customisation.product.productType;
    return productType;
  }

  getCustomisationForExcludedProductFeatures(): string[]{
    const excludedProductFeatures = this.customisation.product.excludedFeatures;
    return excludedProductFeatures;
  }

  getCustomisationForShowProductSelectionMinimum(): boolean {
    const showProductSelectionMinimum = this.customisation.product.showProductSelectionMinimum;
    return showProductSelectionMinimum;
  }

  getCustomisationForShowProductSelectionRecommendation(): boolean {
    const showRecommended = this.customisation.product.showRecommended;
    return showRecommended;
  }

  getCustomisationForFilterByProductGroup(): boolean {
    const filterByProductGroup = this.customisation.product.filterByProductGroup;
    return filterByProductGroup;
  }

  getCustomisationForDisableProductsByMaxLoanAmount(): boolean {
    const disableProductsByMaxLoanAmount =
      this.customisation.product.disableProductsByMaxLoanAmount;
    return disableProductsByMaxLoanAmount;
  }

  getCustomisationForSkipAmountAndShowPackageSummary(): boolean {
    const skipAmountAndShowPackageSummary = this.customisation.product.skipAmountAndShowPackageSummary;
    return skipAmountAndShowPackageSummary;
  }

  getCustomisationForProductSelectionMaximum(): number {
    const productSelectionMaximum = this.customisation.product.PRODUCT_SELECTION_MAXIMUM;
    return productSelectionMaximum;
  }

  getCustomisationForProductDetails(): ProductDetails[] {
    const productDetails = this.customisation.product.details;
    return productDetails;
  }

  getCustomisationForReadOnlyFees(): ProductFeeType[] {
    const readOnlyFees = this.customisation.product.readOnlyFees;
    return readOnlyFees;
  }

  getCustomisationForPaygIncomeDetails(): PaygIncomeAttributes[] {
    const paygIncomeDetails = this.customisation.income.paygIncomeDetails;
    return paygIncomeDetails;
  }

  getCustomisationForExcludePreviousYearByDefault(): boolean {
    return this.customisation.income.excludePreviousYearByDefault;
  }

  getCustomisationForAssetTypes(): AssetType[] {
    const assetTypes = this.customisation.assets.assetTypes;
    return assetTypes;
  }

  getCustomisationForLimitSingleProductSelection(): boolean {
    return this.customisation.product.limitSingleProductSelection;
  }
}
